import ajax, { pythonServer } from "./ajax";

export function requestShops() {
  return ajax("/shops");
}

export function requestSellerStores(status) {
  return ajax("/shops/seller-stores", status ? { query: { status } } : undefined);
}

// Seller store delete lifecycle (admin). See riverr-enterprise-nodejs
// docs/SELLER-STORE-DELETE.md. Each takes up to 100 store ids and returns
// { results: [{ id, name, platform, status, reason }], summary }.
export function previewSellerStoreDelete(ids) {
  return ajax("/shops/seller-stores/delete-preflight", {
    method: "POST",
    data: { ids },
    timeout: 60000,
  });
}

export function deleteSellerStores(ids, { riskAck = false } = {}) {
  return ajax("/shops/seller-stores/delete", {
    method: "POST",
    data: { ids, riskAck },
    timeout: 120000,
  });
}

export function restoreSellerStores(ids) {
  return ajax("/shops/seller-stores/restore", {
    method: "POST",
    data: { ids },
    timeout: 60000,
  });
}

export function purgeSellerStores(ids) {
  return ajax("/shops/seller-stores/purge", {
    method: "POST",
    data: { ids },
    timeout: 120000,
  });
}

export function requestListingStores() {
  return ajax("/shops/listing-stores");
}

export function requestShopDetails(id, query) {
  return ajax(`/shops/${id}`, { query });
}

/** Per-MyCart-shop analytics dashboard (native Riverr metrics). */
export function requestShopAnalytics(id, query, options = {}) {
  return ajax(`/shops/${id}/analytics`, {
    query,
    server: "node",
    signal: options.signal,
  });
}

/** DSN.1/DSN.2 — variant/media ceilings for multi-design authoring math. */
export function getShopChannelLimits(id) {
  return ajax(`/shops/${id}/channel-limits`, { server: "node" });
}

export function getShopByUid(uid) {
  return ajax(`/shops/by-uid/${uid}`);
}

export function addShop(data) {
  return ajax(`/shops`, { method: "POST", data });
}

export function getShopHasPopup() {
  return ajax(`/shops/has-popup`, { server: "node" });
}

export function getCopyPreview(id) {
  return ajax(`/shops/${id}/copy-preview`);
}

export function copyShop(id, data) {
  return ajax(`/shops/${id}/copy`, { method: "POST", data });
}

export function getCopyJob(jobId) {
  return ajax(`/shops/copy-jobs/${jobId}`);
}

export function checkDomainAvailability(slug) {
  return ajax(`/shops/check-domain?domain=${encodeURIComponent(slug)}`, { server: "node" });
}

/**
 * Resume the anonymous Auth session that owns an unclaimed popup store so
 * /public-store/:id/complete-signup converts the correct uid.
 */
export function bootstrapUnclaimedStoreClaimSession(shopId) {
  return ajax(`/shops/${shopId}/claim-session`, {
    method: "POST",
    server: "node",
  });
}

export function updateShop(id, data) {
  return ajax(`/shops/${id}`, { method: "PUT", data });
}

export function updateShopHomePageSettings(id, data) {
  return ajax(`/shops/${id}/home-page-settings`, { method: "PUT", data });
}

export function getShopHomePageSettings(id, query) {
  return ajax(`/shops/${id}/home-page-settings`, { query });
}

export function deleteShop(id) {
  return ajax(`/shops/${id}`, { method: "DELETE" });
}

export function requestEtsyAuthUrl() {
  return ajax("/etsy/auth", { server: "python" });
}
export function requestEtsyAuthUrlV2() {
  return ajax("/v2/etsy/auth", { server: "python" });
}
export function requestAmazonAuthUrl() {
  return ajax("/amazon/auth", { server: "python" });
}
export function requestBigcartelAuth() {
  return ajax("/bigcartel/auth", { server: "python" });
}
export function addBigcartelToken(data) {
  return ajax(`/bigcartel/token`, { method: "POST", data, server: "python" });
}
export function requestSquarespaceAuth() {
  return ajax("v2/squarespace/auth", { server: "python" });
}
export function addSquarespaceToken(data) {
  return ajax(`v2/squarespace/token`, {
    method: "POST",
    data,
    server: "python",
  });
}
export function requestSquareAuthUrl() {
  return ajax("/square/auth_url", { server: "python" });
}

export function squareCallback(data) {
  return ajax(`/square/callback`, { data, server: "python", method: "POST" });
}

export function addEtsyAuthToken(data) {
  return ajax(`/etsy/auth_token`, { method: "POST", data, server: "python" });
}
export function addEtsyAuthTokenV2(data) {
  return ajax(`/v2/etsy/token`, {
    method: "POST",
    data,
    server: "python",
  });
}

export function addAmazonAuthToken(data) {
  return ajax(`/amazon/token`, { method: "POST", data, server: "python" });
}

export function addShopifyToken(data) {
  return ajax(`/shopify/token`, { method: "POST", data, server: "python" });
}

export function saveShopifyConnectIntent() {
  return ajax(`/shopify/connect-intent`, {
    method: "POST",
    data: {},
    server: "python",
  });
}

export function shopifyConnectBrowserUrl(token, appStoreUrl) {
  const next = encodeURIComponent(appStoreUrl || "");
  return `${pythonServer}/shopify/connect-browser?token=${encodeURIComponent(
    token || ""
  )}&next=${next}`;
}

export function addShopifyAuth(query) {
  return ajax(`/shopify/auth`, { query, server: "python" });
}
export function addShopifyAnonyAuth(query) {
  return ajax(`/shopify/anonymous-auth`, { query, server: "python" });
}
export function addShopifyAnonyToken(data) {
  return ajax(`/shopify/anonymous-token`, {
    method: "POST",
    data,
    server: "python",
    // App Store install must not inherit another printer's session.
    skipAuth: true,
  });
}

export function addWoocommerceAuth(query) {
  return ajax(`/woocommerce/auth`, { query, server: "python" });
}

export function requestProductShops(query) {
  return ajax(`/shops/product-shops`, { query });
}

export function requestEtsyCategories(query) {
  return ajax(`/etsy/taxonomy`, { query, server: "python" });
}

export function requestEtsyTemplates(query) {
  return ajax(`/etsy/shipping-templates`, { query, server: "python" });
}

export function requestEtsyTaxonomyV2(query) {
  return ajax(`/v2/etsy/taxonomy`, { query, server: "python" });
}

export function requestEtsyShippingProfiles(query) {
  return ajax(`/v2/etsy/shipping-profiles`, { query, server: "python" });
}

export function addToChannel(data) {
  return ajax(`/products/submit`, { method: "POST", data, server: "python" });
}

export function requestEbayAuth() {
  return ajax("v2/ebay/auth", { server: "python" });
}
export function addEbayToken(data) {
  return ajax(`v2/ebay/token`, { method: "POST", data, server: "python" });
}
export function shopifySubscription(query) {
  return ajax(`/shopify/subscription`, {
    method: "POST",
    query,
    server: "python",
  });
}

export function requestShipStationShops(query) {
  return ajax("/shipstation/stores", { server: "python", query });
}

export function saveShipStationAuth(data) {
  return ajax("/shipstation/auth", { method: "POST", server: "python", data });
}

export function saveOrderDeskAuth(data) {
  return ajax("/v2/orderdesk/auth", { method: "POST", server: "python", data });
}

export function addShopAuth(query) {
  return ajax(`/v2/shops/auth`, { query, server: "python" });
}

export function getProductsOfShop(query) {
  return ajax(`/products/store-products`, { query });
}

export function updateProductVisibility(data) {
  return ajax(`/products/store-products`, { data, method: "PUT" });
}

// Removes listings from one store only. The catalog product and every other
// store keep their copy. Body: { shopId, ids }.
export function removeStoreProducts({ shopId, ids }) {
  return ajax(`/products/store-products/remove`, {
    method: "POST",
    data: { shopId, ids },
  });
}

// Copies catalog owned fields (description, images) onto store listings.
// dryRun true only reports what would change. Body: { shopId, ids, dryRun }.
export function syncStoreProductsFromCatalog({ shopId, ids, dryRun = true }) {
  return ajax(`/products/store-products/sync`, {
    method: "POST",
    data: { shopId, ids, dryRun },
  });
}

export function connectShop(data) {
  return ajax(`/shops/connect-shops`, { method: "POST", data });
}

export function updateShopDomains(id, data) {
  return ajax(`/v2/shops/${id}/domains`, {
    method: "POST",
    data,
    server: "python",
  });
}

export function verifyShopDomains(id) {
  return ajax(`/v2/shops/${id}/domains/verify`, {
    method: "POST",
    server: "python",
  });
}

export function detectShopDnsHost(id, domain) {
  return ajax(`/v2/shops/${id}/domains/dns-host`, {
    method: "POST",
    data: { domain },
    server: "python",
  });
}

export function startDomainConnect(id, data) {
  return ajax(`/v2/shops/${id}/domains/connect/start`, {
    method: "POST",
    data,
    server: "python",
  });
}

export function getPopupStoreOpportunities(id, query) {
  return ajax(`/shops/${id}/popup-store-opportunities`, { query });
}

export function getPopupStoreOpportunityDetail(shopId, customerDocId) {
  return ajax(
    `/shops/${encodeURIComponent(shopId)}/popup-store-opportunities/${encodeURIComponent(
      customerDocId
    )}`
  );
}

export function getEnterprisePopupStoreOpportunities(query) {
  return ajax(`/shops/popup-store-opportunities`, { query });
}

export function updatePopupStoreRecoverySettings(id, data) {
  return ajax(`/shops/${encodeURIComponent(id)}/recovery-settings`, {
    method: "PATCH",
    data,
  });
}

export function getOpportunityRecoverySummary(shopId, opportunityId) {
  return ajax(
    `/shops/${encodeURIComponent(shopId)}/opportunities/${encodeURIComponent(
      opportunityId
    )}/recovery`
  );
}

export function sendOpportunityFollowUp(shopId, opportunityId, data) {
  return ajax(
    `/shops/${encodeURIComponent(shopId)}/opportunities/${encodeURIComponent(
      opportunityId
    )}/follow-up`,
    { method: "POST", data }
  );
}

export function markOpportunityOutcome(shopId, opportunityId, data) {
  return ajax(
    `/shops/${encodeURIComponent(shopId)}/opportunities/${encodeURIComponent(
      opportunityId
    )}/outcome`,
    { method: "POST", data }
  );
}

export function dismissOpportunityReview(shopId, opportunityId) {
  return ajax(
    `/shops/${encodeURIComponent(shopId)}/opportunities/${encodeURIComponent(
      opportunityId
    )}/dismiss-review`,
    { method: "POST", data: {} }
  );
}

export function setOpportunityArchived(shopId, opportunityId, data) {
  return ajax(
    `/shops/${encodeURIComponent(shopId)}/opportunities/${encodeURIComponent(
      opportunityId
    )}/archive`,
    { method: "POST", data }
  );
}

export function updateOpportunityAutomation(shopId, opportunityId, data) {
  return ajax(
    `/shops/${encodeURIComponent(shopId)}/opportunities/${encodeURIComponent(
      opportunityId
    )}/automation`,
    { method: "PATCH", data }
  );
}
